import { Gauge, Fuel, Cog, Calendar, Star, MessageSquare } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";
import { askAboutCar } from "@/lib/enquiry";

const FALLBACK =
    "https://images.pexels.com/photos/4480505/pexels-photo-4480505.jpeg?auto=compress&cs=tinysrgb&w=800";

export default function CarCard({ car, onOpen }) {
    const img = resolveMediaUrl(car.image_urls?.[0]) || FALLBACK;
    const km = car.mileage ? `${car.mileage.toLocaleString()} km` : "—";

    const handleAsk = (e) => {
        e.stopPropagation();
        askAboutCar(car);
    };

    return (
        <div
            onClick={onOpen}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen?.();
                }
            }}
            data-testid={`car-card-${car.id}`}
            className="text-left bg-[#18181a] border border-white/10 card-hover overflow-hidden group cursor-pointer flex flex-col"
        >
            <div className="relative aspect-[16/10] overflow-hidden bg-black">
                <img
                    src={img}
                    alt={`${car.make} ${car.model}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                />
                {car.featured && (
                    <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-black text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 font-bold shadow-lg"
                        data-testid={`car-featured-badge-${car.id}`}
                    >
                        <Star className="w-3 h-3 fill-black" /> Featured
                    </span>
                )}
                <span
                    className={`absolute top-3 right-3 text-[10px] uppercase tracking-[0.2em] px-2 py-1 font-bold ${
                        car.condition === "Sold"
                            ? "bg-zinc-700 text-zinc-300"
                            : car.condition === "Available"
                              ? "bg-emerald-600 text-white"
                              : "bg-[#d52b1e] text-white"
                    }`}
                >
                    {car.condition}
                </span>
                <span className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1">
                    {car.body_type || "Vehicle"}
                </span>
            </div>
            <div className="p-5 flex flex-col flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    {car.year}
                </div>
                <div className="font-display text-2xl uppercase leading-tight mt-1">
                    {car.make}
                </div>
                <div className="font-display text-xl uppercase text-[#d52b1e] leading-tight mb-4">
                    {car.model}
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-zinc-300 border-t border-white/10 pt-4">
                    <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                        {car.year}
                    </div>
                    <div className="flex items-center gap-2">
                        <Gauge className="w-3.5 h-3.5 text-zinc-500" />
                        {km}
                    </div>
                    <div className="flex items-center gap-2">
                        <Fuel className="w-3.5 h-3.5 text-zinc-500" />
                        {car.fuel}
                    </div>
                    <div className="flex items-center gap-2">
                        <Cog className="w-3.5 h-3.5 text-zinc-500" />
                        {car.transmission}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={handleAsk}
                    data-testid={`car-ask-btn-${car.id}`}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-transparent hover:bg-[#d52b1e] border border-[#d52b1e] text-[#d52b1e] hover:text-white transition-colors py-2.5 uppercase text-[11px] tracking-[0.25em] font-bold"
                >
                    <MessageSquare className="w-3.5 h-3.5" /> Ask about this car
                </button>
            </div>
        </div>
    );
}
